import { _ } from "libs";

import React, { useEffect, useState } from "react";
import withRedux from "../../withRedux";
import {
	socialFind,
	addLocation,
	updateLocationPlayersCount,
	addLocationError,
	clearLocationError,
	updateCurrentLocationCoordinates,
} from "../../actions";
import { Profile, User, Location, Club } from "../../types";
import UsersList from "../usersLists";
import PlayerFilter, { defaultFilter, PlayersFilter } from "../playerFilter";
import PlayerSidebar from "../playerSidebar";
import LocationDropdown from "./locationDropdown";
import Loader from "../loader";
import { persistFilter, hydrateFilter } from "../../libs/filterStorage";
import { InvisibleInfiniteLoader } from "../infiniteLoader";
import NotInPlayerNetwork from "../notInPlayerNetwork";

function mergeFilterWithParams(filter: PlayersFilter, params) {
	const [ageFrom, ageTo] = filter.age;
	const [defaultAgeFrom, defaultAgeTo] = defaultFilter.age;
	let age = { ageFrom, ageTo } as any;
	if (ageFrom === defaultAgeFrom && ageTo === defaultAgeTo) {
		age = {};
	}
	return { ...params, ...age, gender: filter.gender, skill: filter.skill };
}

type AddLocation = typeof addLocation;
type UpdateLocationPlayersCount = typeof updateLocationPlayersCount;
type AddLocationError = typeof addLocationError;

const FILTER_NAME = "everyoneFilter";
const PAGE_LIMIT = 50;

const Everyone = ({
	profile,
	addLocation,
	updateLocationPlayersCount,
	locations,
	addLocationError,
	clearLocationError,
	updateCurrentLocationCoordinates,
}: {
	profile: Profile;
	addLocation: AddLocation;
	updateLocationPlayersCount: UpdateLocationPlayersCount;
	addLocationError: AddLocationError;
	clearLocationError;
	locations: Location[];
	updateCurrentLocationCoordinates;
}) => {
	const [loading, setLoading] = useState(false);
	const [users, setUsers] = useState([]);
	const [selectedUser, selectUser] = useState(null as User);
	const [selectedLocation, selectLocation] = useState(null);
	const [currentPage, setPage] = useState(0);
	const [hasMoreResults, setHasMoreResults] = useState(true);
	const [params, setParams] = useState(
		mergeFilterWithParams(hydrateFilter(FILTER_NAME, defaultFilter, profile.userId), {
			latitude: locations[0].lat,
			longitude: locations[0].lng,
			range: 10,
		})
	);

	const search = async (page: number) => {
		setLoading(true);
		let searchLocation = selectedLocation;

		if (selectedLocation === null) {
			searchLocation = locations[0];
			selectLocation(searchLocation);
		}

		try {
			const { data, pageRows, pageCurrent } = await socialFind(params, page, PAGE_LIMIT);
			updateLocationPlayersCount(searchLocation, pageRows);
			const newUsers = pageCurrent === 0 ? [...data] : [...users, ...data];
			setUsers(newUsers);

			if (newUsers.length < pageRows) {
				setPage(currentPage + 1);
			} else {
				setHasMoreResults(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		search(0);
	}, []);

	useEffect(() => {
		setPage(0);
		setHasMoreResults(true);
		setUsers([]);
		search(0);
	}, [params]);

	const onFilterChange = (filter: PlayersFilter) => {
		persistFilter(FILTER_NAME, filter, profile.userId);
		setParams(mergeFilterWithParams(filter, params));
	};

	const closePlayerSidebar = () => {
		selectUser(null);
	};

	const onLocationChange = (location: Location) => {
		selectLocation(location);

		if (location.placeId === "around_me") {
			if ("geolocation" in navigator) {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						setParams({
							...params,
							latitude: position.coords.latitude,
							longitude: position.coords.longitude,
						});
						clearLocationError(location);
						updateCurrentLocationCoordinates(location, position.coords.latitude, position.coords.longitude);
					},
					(error: PositionError) => {
						addLocationError(location, error);
						selectLocation(locations[1]);
					}
				);
			} else {
				console.log("geolocation not available");
			}
		} else {
			setParams({ ...params, latitude: location.lat, longitude: location.lng });
		}
	};

	const onViewChange = async (inView: boolean, observerEntry) => {
		const shouldLoad = inView && hasMoreResults;

		if (shouldLoad) {
			search(currentPage);
		}
	};

	return (
		<div className="contentWrapper locationPlayersPage">
			<div className="contentLeft">
				{profile.useNetwork ? (
					[
						<div className="playersHeader" key="header">
							<LocationDropdown
								locations={locations}
								selectedLocation={selectedLocation}
								onChange={onLocationChange}
								onSelect={addLocation}
							/>
							<PlayerFilter
								onFilterChange={onFilterChange}
								value={hydrateFilter(FILTER_NAME, defaultFilter, profile.userId)}
							/>
						</div>,
						<div className="playersBody" key="body">
							{users && users.length > 0 ? (
								<UsersList
									users={users || []}
									onSelect={selectUser}
									key="list"
									selectedUser={selectedUser}
								/>
							) : null}
							{(!users || users.length == 0) && !loading ? (
								<div className="noResults">{_("players.location.noResults.heading")}</div>
							) : null}
							<InvisibleInfiniteLoader onViewChange={onViewChange} />
							{loading ? <Loader /> : null}
						</div>,
					]
				) : (
					<NotInPlayerNetwork />
				)}
			</div>
			<div className="contentRight">
				{selectedUser ? <PlayerSidebar user={selectedUser} onClose={closePlayerSidebar} /> : null}
			</div>
		</div>
	);
};

const mapStateToProps = ({ profile, locations }, ownProps) => {
	return { profile, locations };
};

export default withRedux(
	{ addLocation, updateLocationPlayersCount, addLocationError, clearLocationError, updateCurrentLocationCoordinates },
	mapStateToProps
)(Everyone);
